import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ParamMapService } from '../../../Services/param-map.service';
import { FormFlowService } from '../../../../../../Services/formFlow.service';
import { TenantService } from '../../../../../../Services';
import { FormFlowFieldTypes } from '../../../../../../Constants/FormFlowFieldTypes';
import { ModuleService } from '../../../Services/module.service';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { ListItem } from '../../../Models/list-item';
import { distinctUntilKeyChanged, filter } from 'rxjs/operators';
import { Field, IPrimaryFieldConfiguration } from '../../../Models';
import { MatFormField, MatLabel, MatHint } from '@angular/material/input';
import { MatSelect } from '@angular/material/select';
import { FormsModule } from '@angular/forms';
import { NgClass, AsyncPipe } from '@angular/common';
import { MatOption } from '@angular/material/autocomplete';
import { MatMiniFabButton } from '@angular/material/button';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { MatIcon } from '@angular/material/icon';
import { TranslationPipe } from '../../../../../../Services/translation.pipe';

@Component({
    selector: 'aw-primary-field-config',
    templateUrl: './primary-field-config.component.html',
    styleUrls: ['./primary-field-config.component.less'],
    imports: [MatFormField, MatLabel, MatSelect, FormsModule, MatOption, MatHint, MatMiniFabButton, NgClass, ExtendedModule, MatIcon, AsyncPipe, TranslationPipe]
})

export class PrimaryFieldConfigComponent implements OnInit, OnDestroy {
    moduleId: string;
    version: number;
    stepId: string;
    multiItemLevels: Field[];
    primaryFieldConfiguration: IPrimaryFieldConfiguration;
    drivingFields: Field[];
    drivingFieldValues: ListItem[];
    availableFields: Field[];
    private fieldsReady: boolean = null;

    private listAlias: string = "ListAlias";
    private getMultiFields: boolean = false;

    constructor(private activatedRoute: ActivatedRoute,
        private readonly tenantService: TenantService,
        public moduleService: ModuleService,
        private formFlowService: FormFlowService,
        public paramMapService: ParamMapService) { }

    private moduleSubscription: Subscription;
    private activatedRouteSub: Subscription;

    ngOnInit() {
        this.moduleId = this.paramMapService.getParam(this.activatedRoute, "moduleId");
        this.version = parseInt(this.paramMapService.getParam(this.activatedRoute, "version"));
        this.stepId = this.paramMapService.getParam(this.activatedRoute, "stepId");
        this.primaryFieldConfiguration = new IPrimaryFieldConfiguration;

        this.activatedRouteSub = this.activatedRoute.queryParamMap.subscribe(queryParams => {
            this.getMultiFields = (queryParams.get('getMultiFields') == 'true');
        });

        this.moduleSubscription = combineLatest(
            this.activatedRoute.queryParams,
            this.moduleService.module().pipe(
                filter(module => module !== null),
                distinctUntilKeyChanged('ModuleId')
            )
        ).subscribe(([queryParams, result]) => {
            this.stepId = queryParams.stepId; if (this.getMultiFields === true) {
                this.moduleService.getStepMultiFields(this.stepId).then(() => {
                    this.fieldsReady = true;
                });
            } else {
                this.moduleService.getStepFields(this.stepId).then(() => {
                    this.fieldsReady = true;
                });
            }
        });

        this.setMultiItemLevels();
    }

    ngOnDestroy(): void {
        if (this.moduleSubscription !== null) {
            this.moduleSubscription.unsubscribe();
        }
        if (this.activatedRouteSub !== null) {
            this.activatedRouteSub.unsubscribe();
        }
    }

    levelSelectionChanged(): void {
        this.setDrivingFields();
        this.primaryFieldConfiguration.DrivingField = new Field;
    }

    drivingFieldSelectionChanged(): void {
        this.getDrivingFieldValues();
        this.setAvailableFields();
    }

    togglePrimaryField(primaryField: Field, drivingField: ListItem): void {
        let shouldAddRule = true;
        if (!this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields || !this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields.PrimaryFieldRules) {
            this.initialisePrimaryField(this.primaryFieldConfiguration.MultiItemLevel);
        } else {
            shouldAddRule = this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields.PrimaryFieldRules[drivingField.Value] !== primaryField.FieldId;
        }
        if (shouldAddRule) {
            this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields.DrivingFieldParentPropertyName = this.primaryFieldConfiguration.DrivingField.FieldId;
            this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields.PrimaryFieldRules[drivingField.Value] = primaryField.FieldId;

            this.removePreviousPrimaryField(this.primaryFieldConfiguration.MultiItemLevel, drivingField);
            const multiParentPropertyName = this.primaryFieldConfiguration.MultiItemLevel.FieldId;
            this.moduleService.updatePrimaryFieldRules(this.tenantService.selectedTenant, this.moduleId, this.version, this.stepId, multiParentPropertyName, this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields).then(() => {
                this.getPrimaryFieldState(primaryField, drivingField);
            });
        }
    }

    getPrimaryFieldState(primaryField: Field, drivingField: ListItem): boolean {
        const selectedPrimaryField = this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields
            && this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields.PrimaryFieldRules
            && this.primaryFieldConfiguration.MultiItemLevel.PrimaryFields.PrimaryFieldRules[drivingField.Value]
        return selectedPrimaryField ? selectedPrimaryField === primaryField.FieldId : false;
    }

    private removePreviousPrimaryField(primaryField: Field, drivingField: ListItem) {
        this.availableFields
            .filter(field => field !== primaryField && field.PrimaryFields)
            .forEach(field => {
                delete field.PrimaryFields.PrimaryFieldRules[drivingField.Value];
            });
    }

    private initialisePrimaryField(field: Field): void {
        if (!field.PrimaryFields) {
            field.PrimaryFields = {
                DrivingFieldParentPropertyName: null,
                UseMultipleTypePropertyName: false,
                PrimaryFieldRules: {}
            }
        } else if (!field.PrimaryFields.PrimaryFieldRules) {
            field.PrimaryFields.PrimaryFieldRules = {}
        }
    }

    private setMultiItemLevels(): void {
        this.formFlowService.getFieldsOfTypeMulti(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version)
            .subscribe((result) => {
                this.multiItemLevels = result;
            });
    }

    private setDrivingFields(): void {
        if (this.primaryFieldConfiguration && this.primaryFieldConfiguration.MultiItemLevel) {
            this.formFlowService.getFieldsOnMulti(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, this.primaryFieldConfiguration.MultiItemLevel.FieldId)
                .subscribe((result) => {
                    this.drivingFields = result.filter(r => r.FieldUniqueId === FormFlowFieldTypes.List);
                });
        }
    }

    private setAvailableFields(): void {
        if (this.primaryFieldConfiguration && this.primaryFieldConfiguration.MultiItemLevel) {
            this.formFlowService.getFieldsOnMulti(this.tenantService.selectedTenant.TenantId, this.moduleId, this.version, this.primaryFieldConfiguration.MultiItemLevel.FieldId)
                .subscribe((result) => {
                    this.availableFields = result.filter(r => r.FieldUniqueId !== FormFlowFieldTypes.MultiItem);
                });
        }
    }

    private getDrivingFieldValues() {
        (this.tenantService.getListItems(["AIRSWEBLIST", this.primaryFieldConfiguration.DrivingField.ListParams[this.listAlias]]) as Observable<ListItem[]>)
            .subscribe(items => {
                this.drivingFieldValues = items;
            });
    }
}